import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { EMPTY, Observable, catchError, finalize, tap } from 'rxjs';
import {
  LoadK365SetupGuidesAccessIfNotLoaded,
  SetK365SetupGuidesAccessInactive,
} from '@layout/k365-setup-guides-access/store/actions/k365-setup-guides-access.actions';
import { K365SetupGuidesAccessService } from '@layout/k365-setup-guides-access/services/k365-setup-guides-access.service';

interface K365SetupGuidesAccessStateModel {
  setupGuidesAccess?: boolean;
  loading: boolean;
  error: boolean;
}

@State<K365SetupGuidesAccessStateModel>({
  name: 'k365SetupGuidesAccess',
  defaults: {
    loading: false,
    error: false,
  },
})
@Injectable()
export class K365SetupGuidesAccessState {
  private readonly k365SetupGuidesAccessService = inject(K365SetupGuidesAccessService);

  @Selector()
  static setupGuidesAccess({ setupGuidesAccess }: K365SetupGuidesAccessStateModel): boolean | undefined {
    return setupGuidesAccess;
  }

  @Selector()
  static loading({ loading }: K365SetupGuidesAccessStateModel): boolean {
    return loading;
  }

  @Selector()
  static error({ error }: K365SetupGuidesAccessStateModel): boolean {
    return error;
  }

  @Action(SetK365SetupGuidesAccessInactive)
  setK365SetupGuidesAccessInactive(context: StateContext<K365SetupGuidesAccessStateModel>): void {
    context.patchState({ setupGuidesAccess: false, loading: false, error: false });
  }

  @Action(LoadK365SetupGuidesAccessIfNotLoaded)
  loadK365SetupGuidesAccessIfNotLoaded(context: StateContext<K365SetupGuidesAccessStateModel>): Observable<boolean> {
    const { setupGuidesAccess, loading } = context.getState();

    if (loading) {
      return EMPTY;
    }

    if (setupGuidesAccess !== undefined) {
      return EMPTY;
    }

    context.patchState({ loading: true });

    return this.k365SetupGuidesAccessService.getSetupGuidesAccess().pipe(
      tap(hasAccess => context.patchState({ setupGuidesAccess: hasAccess, error: false })),
      catchError(() => {
        context.patchState({ setupGuidesAccess: undefined, error: true });
        return EMPTY;
      }),
      finalize(() => context.patchState({ loading: false }))
    );
  }
}
